import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { transactionApi } from "../../repositories/transaction";
import {adminApi} from "../../repositories/admin";
import Pagination from "../../components/common/Pagination";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import DataTable, { DataTableColumn, rowActionIconClass } from "../../components/ui/DataTable";
import SegmentedTabs, { SegmentedTabItem } from "../../components/ui/SegmentedTabs";
import RowActionsMenu, { RowMenuItem } from "../../components/ui/RowActionsMenu";
import { badgeBaseClass, badgeTones } from "../../components/ui/StatusBadge";
import { cn } from "../../DashboardPage/lib/utils";
import Button from "../../components/ui/Button";
import {
  RefreshCw,
  Search,
  Filter,
  Download,
  Copy,
  Check,
  ChevronDown,
  X as XIcon,
  Mail,
} from "lucide-react";

interface Transaction {
  id: string;
  email: string;
  productId: string;
  transactionId: string;
  status: string;
  pitchLimit: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface CopiedFieldsState {
  [key: string]: boolean;
}

/**
 * Transaction and subscription statuses share StatusBadge's shell and tone
 * tokens rather than its pitch-status map, so "completed" here is literally the
 * same green as "delivered" on /dashboard.
 *
 * Both spellings of cancelled are mapped: the app writes "cancelled" but Stripe
 * returns "canceled", and the old map only had the first — so genuinely
 * cancelled subscriptions fell through to the unknown-status colour.
 */
const TRANSACTION_STATUS_TONES: Record<string, string> = {
  completed: badgeTones.success,
  active: badgeTones.success,
  pending: badgeTones.warning,
  trialing: badgeTones.pending,
  inactive: badgeTones.danger,
  past_due: badgeTones.danger,
  unpaid: badgeTones.danger,
  cancelled: badgeTones.neutral,
  canceled: badgeTones.neutral,
};

const StatusPill: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return <span className="text-neutral-400">N/A</span>;
  const tone = TRANSACTION_STATUS_TONES[status.toLowerCase()] ?? badgeTones.neutral;
  return (
    <span className={cn(badgeBaseClass, tone, "capitalize")}>
      {status.replace(/_/g, " ")}
    </span>
  );
};

/**
 * A long opaque identifier: truncated to the column, full value on hover, and a
 * copy button because the truncated text is not selectable in a useful way.
 */
const CopyableId: React.FC<{
  value?: string | null;
  label: string;
  copied: boolean;
  onCopy: () => void;
}> = ({ value, label, copied, onCopy }) => {
  if (!value) return <span className="text-neutral-400">N/A</span>;
  return (
    <div className="flex items-center gap-1">
      <span className="max-w-[140px] truncate text-neutral-600" title={value}>
        {value}
      </span>
      <button
        type="button"
        onClick={onCopy}
        className={rowActionIconClass}
        title={copied ? "Copied" : `Copy ${label}`}
        aria-label={copied ? `${label} copied` : `Copy ${label}`}
      >
        {copied ? (
          <Check size={16} className="text-success-600" />
        ) : (
          <Copy size={16} />
        )}
      </button>
    </div>
  );
};

const selectClass =
  "w-full py-2 pl-3 pr-9 text-sm text-black transition-colors bg-white border rounded-md outline-none appearance-none cursor-pointer border-neutral-300 focus:border-primary-500";

const selectChevronClass =
  "absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2.5 top-1/2 text-neutral-400";

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : "N/A";

const TransactionPage = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [statusFilter, setStatusFilter] = useState("all");
  const [copiedFields, setCopiedFields] = useState<CopiedFieldsState>({});
  const [tableView, setTableView] = useState<"userTransactions" | "promoUsers" | "subscribedUsers">("userTransactions");
  const [promoUsers, setPromoUsers] = useState<any[]>([]);
  const [searchType, setSearchType] = useState<"email" | "code" | "status">("email");
  const [subscribedUsers, setSubscribedUsers] = useState<any[]>([]);
  // Only set when the API reports a real count; left null so Pagination hides the
  // "Showing x to y of z" line rather than showing an estimate derived from page count.
  const [totalItems, setTotalItems] = useState<number | null>(null);

  const { getAllPromoUsers, getAllSubscribedUsers } = adminApi;

  const fetchTransactions = async (page: number): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await transactionApi.getAllUserTransactions({
        page,
        limit,
        search: searchTerm.trim(),
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      setTransactions(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setPage(response.data.pagination.page);
      setTotalItems(response.data.pagination.totalItems ?? null);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiError?.response?.data?.message || "Failed to fetch transactions",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPromoUsers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getAllPromoUsers({
        page,
        limit,
        search: debouncedSearchTerm,
        searchType
      });


      if (response.data) {
        const promoUsersData = response.data.data?.promoUsers || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};

        setPromoUsers(Array.isArray(promoUsersData) ? promoUsersData : []);
        setTotalPages(paginationData.totalPages || 1);
        setPage(paginationData.page || 1);
        setTotalItems(paginationData.totalItems ?? null);
      } else {
        setPromoUsers([]);
        setTotalPages(1);
        setTotalItems(null);
      }
    } catch (error) {
      console.error("Error fetching promo users:", error);
      setPromoUsers([]);
      const apiError = error as ApiError;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiError?.response?.data?.message || "Failed to fetch promo users",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const getSubscribedUsersData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getAllSubscribedUsers({
        page,
        limit,
        search: debouncedSearchTerm,
        searchType: searchType as "email" | "status",
      });
      if (response.data) {
        const data = response.data.data?.subscribedUsers || [];
        const paginationData = response.data.data?.pagination || {};
        setSubscribedUsers(Array.isArray(data) ? data : []);
        setTotalPages(paginationData.totalPages || 1);
        setPage(paginationData.page || 1);
        setTotalItems(paginationData.totalItems ?? null);
      } else {
        setSubscribedUsers([]);
        setTotalPages(1);
        setTotalItems(null);
      }
    } catch (error) {
      setSubscribedUsers([]);
      const apiError = error as ApiError;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiError?.response?.data?.message || "Failed to fetch subscribed users",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Only let searchType drive a refetch while a search term is active; switching the
  // dropdown on an empty input would otherwise re-run an identical unfiltered query.
  const activeSearchType = debouncedSearchTerm.trim() ? searchType : null;

  useEffect(() => {
    if (tableView === "promoUsers") {
      getPromoUsers();
    } else if (tableView === "subscribedUsers") {
      getSubscribedUsersData();
    } else {
      fetchTransactions(page);
    }
  }, [page, limit, debouncedSearchTerm, statusFilter, tableView, activeSearchType]);

  const handleRefresh = () => {
    if (tableView === "promoUsers") {
      getPromoUsers();
    } else if (tableView === "subscribedUsers") {
      getSubscribedUsersData();
    } else {
      fetchTransactions(page);
    }
  };

  const handleLimitChange = (newLimit: any) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleStatusFilterChange = (e: any) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const copyToClipboard = (text: any, field: any) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedFields({ ...copiedFields, [field]: true });
      setTimeout(() => {
        setCopiedFields({ ...copiedFields, [field]: false });
      }, 2000);
    });
  };

  const handleSendEmail = (transaction: Transaction) => {
    Swal.fire({
      icon: "info",
      title: "Send Email",
      text: `Preparing to send email to ${transaction.email}`,
    });
  };

  const handleToggleTableView = (view: "userTransactions" | "promoUsers" | "subscribedUsers") => {
    setTableView(view);
    setPage(1);
    // Keep the search term so the admin can check whether the same entity exists in the newly selected tab.
    // Reset searchType to "email" so the carried-over term is interpreted consistently across tabs.
    setSearchType("email");
  };

  const tabItems: SegmentedTabItem[] = [
    { key: "userTransactions", label: "Pitch Pack Users" },
    { key: "promoUsers", label: "Promo Users" },
    { key: "subscribedUsers", label: "Subscribed Users" },
  ];

  const idColumn: DataTableColumn<any> = {
    key: "id",
    header: "ID",
    cell: (row) => <span className="font-medium tabular-nums">{row.id}</span>,
  };

  const promoUserColumns: DataTableColumn<any>[] = [
    idColumn,
    {
      key: "email",
      header: "Email",
      cell: (row) => <span className="text-neutral-600">{row.User?.email || "N/A"}</span>,
    },
    {
      key: "code",
      header: "Code",
      cell: (row) => (
        <CopyableId
          value={row.code}
          label="promo code"
          copied={!!copiedFields[`code-${row.id}`]}
          onCopy={() => copyToClipboard(row.code, `code-${row.id}`)}
        />
      ),
    },
    {
      key: "isUsed",
      header: "Is Used",
      cell: (row) => <span className="text-neutral-600">{row.isUsed ? "Yes" : "No"}</span>,
    },
    {
      key: "expiresAt",
      header: "Expires At",
      cell: (row) => <span className="text-neutral-600">{formatDate(row.expiryDate)}</span>,
    },
  ];

  const subscribedUserColumns: DataTableColumn<any>[] = [
    idColumn,
    {
      key: "email",
      header: "Email",
      cell: (row) => <span className="text-neutral-600">{row.email}</span>,
    },
    {
      key: "plan",
      header: "Plan",
      cell: (row) => <span className="text-neutral-600">{row.planName || "N/A"}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (row) => (
        <span className="tabular-nums text-neutral-600">
          {row.amount ? `$${parseFloat(row.amount).toFixed(2)}` : "N/A"}
        </span>
      ),
    },
    {
      key: "interval",
      header: "Interval",
      cell: (row) => (
        <span className="capitalize text-neutral-600">{row.interval || "N/A"}</span>
      ),
    },
    { key: "status", header: "Status", cell: (row) => <StatusPill status={row.status} /> },
    {
      key: "periodStart",
      header: "Period Start",
      cell: (row) => <span className="text-neutral-600">{formatDate(row.currentPeriodStart)}</span>,
    },
    {
      key: "periodEnd",
      header: "Period End",
      cell: (row) => <span className="text-neutral-600">{formatDate(row.currentPeriodEnd)}</span>,
    },
    {
      key: "subscribedAt",
      header: "Subscribed At",
      cell: (row) => <span className="text-neutral-600">{formatDate(row.createdAt)}</span>,
    },
  ];

  const transactionColumns: DataTableColumn<Transaction>[] = [
    idColumn,
    {
      key: "email",
      header: "Email",
      cell: (row) => <span className="text-neutral-600">{row.email}</span>,
    },
    {
      key: "productId",
      header: "Product ID",
      cell: (row) => (
        <CopyableId
          value={row.productId}
          label="product ID"
          copied={!!copiedFields[`productId-${row.id}`]}
          onCopy={() => copyToClipboard(row.productId, `productId-${row.id}`)}
        />
      ),
    },
    {
      key: "transactionId",
      header: "Transaction ID",
      cell: (row) => (
        <CopyableId
          value={row.transactionId}
          label="transaction ID"
          copied={!!copiedFields[`transactionId-${row.id}`]}
          onCopy={() => copyToClipboard(row.transactionId, `transactionId-${row.id}`)}
        />
      ),
    },
    { key: "status", header: "Status", cell: (row) => <StatusPill status={row.status} /> },
    {
      key: "pitchLimit",
      header: "Pitch Limit",
      align: "center",
      cell: (row) => <span className="tabular-nums text-neutral-600">{row.pitchLimit}</span>,
    },
    {
      key: "createdAt",
      header: "Created At",
      cell: (row) => <span className="text-neutral-600">{formatDate(row.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (row) => (
        <RowActionsMenu label={`Actions for ${row.email}`}>
          <RowMenuItem icon={<Mail size={16} />} onClick={() => handleSendEmail(row)}>
            Send Email
          </RowMenuItem>
        </RowActionsMenu>
      ),
    },
  ];

  const columns =
    tableView === "promoUsers"
      ? promoUserColumns
      : tableView === "subscribedUsers"
        ? subscribedUserColumns
        : transactionColumns;

  const rows: any[] =
    tableView === "promoUsers"
      ? promoUsers
      : tableView === "subscribedUsers"
        ? subscribedUsers
        : transactions;

  const emptyTitle =
    tableView === "promoUsers"
      ? "No promo users found"
      : tableView === "subscribedUsers"
        ? "No subscribed users found"
        : "No transactions found";

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Transactions" }]}
        className="mb-6"
        title="Transactions"
        description="Pitch pack purchases, promo codes, and active subscriptions."
        actions={
          <>
            <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button>
              <Download size={16} />
              Export
            </Button>
          </>
        }
      />

      {/*
        Tabs sit above the filters: the tab decides which table is on screen, so
        the filters read as belonging to it rather than the other way round.
      */}
      <SegmentedTabs
        className="mb-4 overflow-x-auto scrollbar-hide"
        items={tabItems}
        value={tableView}
        onChange={(key) => handleToggleTableView(key as typeof tableView)}
        ariaLabel="Transaction views"
      />

      <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
        {/*
          Search type + term as one control: a single border, radius and
          focus-within treatment wrap both, with an inset divider between them.
          Same bar as Past Submissions / Master Roster / Contacts. The Pitch
          Pack tab searches email only, so it renders the input half alone
          inside the same shell rather than a differently-shaped control.
        */}
        <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 lg:max-w-xl">
          {(tableView === "promoUsers" || tableView === "subscribedUsers") && (
            <>
              <div className="relative shrink-0">
                <select
                  className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
                  aria-label="Search by field"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value as "email" | "code" | "status")}
                >
                  {tableView === "promoUsers" ? (
                    <>
                      <option value="email">Email</option>
                      <option value="code">Code</option>
                    </>
                  ) : (
                    <>
                      <option value="email">Email</option>
                      <option value="status">Status</option>
                    </>
                  )}
                </select>
                <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
              </div>
              <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />
            </>
          )}

          <div className="relative flex-1 min-w-0">
            <Search
              size={16}
              className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400"
            />
            <input
              type="text"
              className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
              placeholder={
                tableView === "promoUsers" || tableView === "subscribedUsers"
                  ? `Search by ${searchType}`
                  : "Search by email"
              }
              aria-label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
                className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
              >
                <XIcon size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {tableView === "userTransactions" && (
            <div className="relative w-40">
              <Filter
                size={16}
                className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400"
              />
              <select
                className={cn(selectClass, "pl-9")}
                aria-label="Filter by status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <ChevronDown className={selectChevronClass} />
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm shrink-0 text-neutral-600">Show:</span>
            <div className="relative w-20">
              <select
                className={selectClass}
                aria-label="Rows per page"
                value={limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className={selectChevronClass} />
            </div>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.id}
        loading={isLoading}
        skeletonRows={Math.min(limit, 10)}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">{emptyTitle}</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Try adjusting your search or filters.
            </span>
          </span>
        }
      />

      {/*
        totalItems stays undefined when the API doesn't report a real count, so
        Pagination drops its "Showing x to y of z" line rather than showing an
        estimate — the page buttons still render.
      */}
      {rows.length > 0 && (
        <div className="mt-4">
          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            totalItems={totalItems ?? undefined}
            limit={limit}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionPage;
